<template>
  <div class="w-full">
    <!-- Title -->
    <NuxtLink class="mb-5 block text-center text-2xl font-semibold text-slate hover:underline lg:text-3xl xl:text-4xl" :to="`/${venue.path}`">
      {{ venue.name }}
    </NuxtLink>
    <!-- Images -->
    <div class="relative mb-5 shrink-0">
      <VSlickCarousel v-if="venue.photos && venue.photos.length" :arrows="false" :dots="true" class="overflow-hidden rounded">
        <a v-for="(photo, idx) in venue.photos" :key="idx" :href="`/${venue.path}`" class="relative block overflow-hidden rounded pb-62%" draggable="false">
          <img loading="lazy" class="absolute size-full bg-gray-400 object-cover" draggable="false" :alt="venue.name" :src="photo.photo_rectangular">
        </a>
      </VSlickCarousel>
      <a href="#" class="absolute right-0 top-0 m-2 block p-2">
        <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M8.49933 1.78376C8.33377 1.62162 8.17146 1.46725 8.0124 1.32072C7.72835 1.05409 7.42494 0.82468 7.10203 0.634034C6.77683 0.433687 6.42433 0.27881 6.04646 0.168416C5.65347 0.0536057 5.21948 0 4.75 0C4.09625 0 3.47605 0.124713 2.89465 0.377101C2.32382 0.624898 1.8209 0.964033 1.38994 1.39445C0.956974 1.82164 0.61685 2.32602 0.368825 2.9025L0.367873 2.90474C0.121808 3.48443 0 4.10033 0 4.74763C0 5.37955 0.119249 5.98803 0.357707 6.56931L0.35936 6.57324C0.603002 7.15302 0.94682 7.66575 1.38894 8.10762L8.5 15.2065L15.6113 8.10742C16.0537 7.66519 16.3951 7.15198 16.6337 6.57126C16.8778 5.98968 17 5.38049 17 4.74763C17 4.09972 16.8753 3.48338 16.6238 2.9036C16.381 2.32661 16.0432 1.82183 15.6101 1.39445C15.1791 0.964033 14.6762 0.624898 14.1054 0.377101C13.5239 0.124713 12.9038 0 12.25 0C11.7805 0 11.3465 0.0536057 10.9535 0.168416C10.575 0.279004 10.22 0.434025 9.89017 0.63405C9.57267 0.824559 9.26988 1.05348 8.98156 1.31905L8.97796 1.32243C8.82262 1.46837 8.66307 1.62216 8.49933 1.78376ZM8.85663 4.2548C9.14621 3.96012 9.42297 3.67036 9.68685 3.38553C9.93571 3.1173 10.1923 2.88054 10.4563 2.67424C10.7054 2.47958 10.9768 2.32367 11.2716 2.20581L11.2776 2.20328C11.5449 2.09234 11.8658 2.03067 12.25 2.03067C12.6403 2.03067 12.9949 2.10213 13.3195 2.24052L13.3269 2.24354C13.6634 2.38072 13.9505 2.57083 14.1934 2.81355L14.1998 2.81975C14.4459 3.05713 14.6399 3.341 14.7814 3.67683L14.7823 3.67902C14.9208 4.0033 14.9922 4.35766 14.9922 4.74763C14.9922 5.11564 14.9238 5.46524 14.7871 5.80019C14.6512 6.1331 14.4544 6.42871 14.1934 6.68957L8.5 12.3798L2.80658 6.68957C2.54515 6.42829 2.34523 6.13179 2.20405 5.79758C2.06806 5.46342 2 5.11468 2 4.74763C2 4.36471 2.07074 4.01019 2.20971 3.67938C2.35759 3.3398 2.55418 3.05014 2.79877 2.80569C3.04883 2.55576 3.34068 2.36008 3.67832 2.218L3.68342 2.21578C4.01404 2.07231 4.3679 1.99944 4.75 1.99944C5.11979 1.99944 5.43375 2.06239 5.70072 2.17732L5.70326 2.1784C6.00575 2.30666 6.28133 2.47077 6.5315 2.67079L6.53495 2.6735C6.80495 2.88552 7.06441 3.12523 7.31315 3.39333C7.58313 3.68432 7.86093 3.97255 8.14655 4.258L8.5032 4.61446L8.85663 4.2548Z" fill="white" />
        </svg>
      </a>
    </div>
    <!-- Details -->
    <div class="mb-2">
      <!-- Tripexpert Score -->
      <div class="mb-8 flex items-center">
        <label class="label text-lg lg:text-xl xl:text-2xl">Tripexpert score:</label>
        <VenueRating :venue="venue" width="45" height="48" />
      </div>
      <!-- Location -->
      <div class="mb-8 flex items-start leading-none text-gray-dark lg:text-lg xl:text-xl">
        <label class="label text-lg lg:text-xl xl:text-2xl">Location:</label>
        <span class="leading-tight">{{ venue.address }}</span>
      </div>
      <!-- Price -->
      <div class="mb-8 flex items-start leading-none text-gray-dark lg:text-lg xl:text-xl">
        <label class="label text-lg lg:text-xl xl:text-2xl">Price:</label>
        <div class="leading-tight">
          <span>From <span class="font-semibold">{{ $f.currency(venue.low_rate, currency) }}</span></span>
          <span>/night</span>
        </div>
      </div>
      <!-- Amenities -->
      <div class="mb-8 text-gray-dark">
        <label class="label mb-5 text-lg lg:text-xl xl:text-2xl">Amenities:</label>
        <ul class="amenity-tick-list mb-3 flex flex-wrap">
          <li
            v-for="(amenity, index2) in amenities"
            :key="`amenity-${index2}`"
            class="mb-3 w-1/2"
          >
            {{ amenity.name }}
          </li>
        </ul>
        <a
          v-if="loadMoreAmenities"
          href="javascript:void(0)"
          class="link inline-block text-sm font-medium uppercase text-slate underline"
          @click.prevent="loadMoreAmenities = !loadMoreAmenities"
        >
          show less amenities
        </a>
        <a
          v-else
          href="javascript:void(0)"
          class="link inline-block text-sm font-medium uppercase text-slate underline"
          @click.prevent="loadMoreAmenities = !loadMoreAmenities"
        >
          show all amenities
        </a>
      </div>
      <div class="mb-8 text-sm">
        <label class="label mb-5 text-lg lg:text-xl xl:text-2xl">Expert reviews:</label>
        <VenueReviews :reviews="venue.reviews" />
      </div>
    </div>
    <div v-if="venue.venue_type_id === 1 && venue.booking_url">
      <a
        :href="bookingUrl(venue, filters)"
        target="_blank"
        rel="noopener noreferrer"
        class="letter-sp15 mt-2 block h-10 rounded-md bg-blue px-8 py-1 text-center text-sm uppercase leading-8 text-white hover:bg-blue-dark md:h-12 md:text-lg md:leading-10"
        @click="$gtag.event('book-now')"
      >Book now</a>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { mapState } from 'pinia'
import { VSlickCarousel } from 'v-slick-carousel'
import bookingUrl from '~/mixins/booking-url'
import VenueRating from '~/components/venue/VenueRating'
import VenueReviews from '~/components/venue/VenueReviews'
import { useVenuesStore } from '~/stores/venues'
import { useCurrenciesStore } from '~/stores/currencies'

dayjs.extend(customParseFormat)

export default {
  components: { VenueRating, VenueReviews, VSlickCarousel },

  mixins: [bookingUrl],

  props: {
    venue: { type: Object, required: true },
  },

  data () {
    return {
      loadMoreAmenities: false,
    }
  },

  computed: {
    ...mapState(useVenuesStore, ['filters']),
    ...mapState(useCurrenciesStore, ['currency']),

    featuredHtml () {
      if (! this.venue.featured_extract) { return '' }
      const clean = this.venue.featured_extract.replace(/(<([^>]+)>)/ig, '')
      const idx = clean.lastIndexOf('-')

      return clean.substr(0, idx) + (idx === -1 ? '' : `<span class="italic">${clean.substr(idx).replace('-', '—')}</span>`)
    },

    amenities () {
      if (! this.venue.amenities) { return [] }

      return this.loadMoreAmenities ? this.venue.amenities : this.venue.amenities.filter((e, index) => index < 6)
    },
  },
}
</script>

<style lang="scss" scoped>
  :deep() .label{
    @apply font-semibold text-slate block mr-3;
  }
  :deep() .slick-slider .slider-img {
    -webkit-transition: all ease 0.3s;
    -moz-transition: all ease 0.3s;
    transition: all ease 0.3s;

    &:hover{
      opacity: 0.8;
    }
  }
  :deep() .v-slick-dots {
    bottom: 14px;
    position: absolute;
    li {
      margin: 0;
      button {
        &:before { color: white; opacity: 1; font-size: 9px; }
      }
    }
  }
  :deep() .v-slick-dots li.active button:before {
    @apply text-gray-1 opacity-100;
  }
  ul.amenity-tick-list > li{
    position: relative;
    padding-left: 25px;

    &:before{
      content: "";
      width: 17px;
      height: 12px;
      position: absolute;
      top: 5px;
      left: 0;
      background: url('~/assets/images/blue-tick.svg');
    }
  }
</style>
